import { Mention, SelectedMention } from '../types/mention';

/**
 * MentionParser
 * Utility for parsing, validating, and rendering mentions in content
 */
export class MentionParser {
  /**
   * Parse content and extract mentions with their positions
   *
   * @param content - Raw text content with @mentions
   * @param selectedMentions - User selections from autocomplete
   * @returns Array of Mention objects with calculated indices
   */
  static parse(content: string, selectedMentions: SelectedMention[]): Mention[] {
    const mentions: Mention[] = [];

    // Filter out invalid selectedMentions
    const validSelected = selectedMentions.filter(
      s => s && s.userId && s.username && typeof s.userId === 'string'
    );

    // Sort selected mentions by position (important for correct index calculation)
    const sorted = [...validSelected].sort((a, b) => a.position - b.position);

    // Extract mentions and calculate their positions in the content
    for (const selected of sorted) {
      // Find the @mention pattern starting at the position
      const mentionPattern = `@${selected.username}`;
      const startIndex = content.indexOf(mentionPattern, selected.position);

      if (startIndex !== -1) {
        mentions.push({
          userId: selected.userId,
          username: selected.username,
          startIndex: startIndex,
          endIndex: startIndex + mentionPattern.length,
        });
      }
    }

    return mentions;
  }

  /**
   * Render content with mentions as HTML
   * Replaces @Username with clickable links
   *
   * @param content - Raw text content
   * @param mentions - Mention metadata
   * @param onMentionClick - Optional click handler (userId) => void
   * @returns HTML string with clickable mentions
   */
  static renderHTML(
    content: string,
    mentions: Mention[],
    onMentionClick?: (userId: string) => void
  ): string {
    if (!mentions || mentions.length === 0) {
      return this.escapeHtml(content);
    }

    // Sort mentions by startIndex (reverse order for replacement)
    const sorted = [...mentions].sort((a, b) => b.startIndex - a.startIndex);

    let result = content;

    // Replace mentions from end to start to preserve indices
    for (const mention of sorted) {
      const before = result.substring(0, mention.startIndex);
      const after = result.substring(mention.endIndex);
      const mentionText = `@${mention.username}`;

      // Create clickable mention link
      const clickHandler = onMentionClick
        ? `onclick="handleMentionClick('${mention.userId}')"`
        : '';

      const mentionHtml = `<span class="mention" data-user-id="${mention.userId}" ${clickHandler} style="color: #1976D2; font-weight: 500; cursor: pointer;">${mentionText}</span>`;

      result = before + mentionHtml + after;
    }

    return result;
  }

  /**
   * Render content with mentions as plain text
   * For notifications and non-HTML contexts
   *
   * @param content - Raw text content
   * @param mentions - Mention metadata (not used, included for consistency)
   * @returns Plain text
   */
  static renderPlainText(content: string, mentions: Mention[]): string {
    // For plain text, just return content as-is
    // The @Username format is already readable
    return content;
  }

  /**
   * Extract content preview for notifications (first 100 chars)
   */
  static getContentPreview(content: string, maxLength: number = 100): string {
    if (content.length <= maxLength) {
      return content;
    }
    return content.substring(0, maxLength) + '...';
  }

  /**
   * Escape HTML to prevent XSS
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Validate mentions (basic validation)
   * More comprehensive validation should be done server-side
   */
  static validateMentions(mentions: Mention[], maxMentions: number = 10): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (mentions.length > maxMentions) {
      errors.push(`Maximum ${maxMentions} mentions allowed per content`);
    }

    // Check for duplicate mentions
    const userIds = mentions.map(m => m.userId);
    const uniqueUserIds = new Set(userIds);
    if (userIds.length !== uniqueUserIds.size) {
      errors.push('Duplicate mentions detected');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export const mentionParser = MentionParser;
